import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { MatListModule } from "@angular/material/list";
import {
  ActivatedRoute,
  type IsActiveMatchOptions,
  type Params,
  RouterLink,
  RouterLinkActive,
} from "@angular/router";

import { map } from "rxjs";

import { Roles } from "@juristech/contracts/auth/roles.constants";
import { BOARD_VIEW_MODE } from "@juristech/contracts/orders/board.types";

import { ThemeService } from "../services/theme.service";
import { TokenService } from "../services/token.service";

/**
 * Theme-matched brand artwork (owner-chosen pair, 2026-07-24): the
 * full-color logo on the light canvas, the white-text variant on dark.
 * Files live in apps/admin-web/public/, copied from
 * apps/web/src/assets/logo/partners/1-D.png / 1-L.png.
 */
const BRAND_LOGO_LIGHT = "deednetwork-logo-light.png";
const BRAND_LOGO_DARK = "deednetwork-logo-dark.png";

/** One nav link — rendered by the generic item block in the template. */
interface SidenavItem {
  readonly label: string;
  /** Material Symbols Rounded ligature name. */
  readonly icon: string;
  readonly route: string;
  readonly queryParams?: Params;
  readonly activeOptions: IsActiveMatchOptions;
  /**
   * Marks the plain order-board link, whose active pill is vetoed while the
   * board shows the payment lanes — see {@link SidenavComponent.paymentViewActive}.
   */
  readonly vetoedByPaymentView?: boolean;
}

/** One labelled nav section; super-admin-only groups are filtered out per role. */
interface SidenavGroup {
  readonly label: string;
  readonly requiresSuperAdmin?: boolean;
  readonly items: readonly SidenavItem[];
}

/** routerLinkActive's default matching — link URL is a subset of the current URL. */
const SUBSET_MATCH: IsActiveMatchOptions = {
  paths: "subset",
  queryParams: "subset",
  fragment: "ignored",
  matrixParams: "ignored",
};

/** Equivalent of `{ exact: true }` — full path + query-param equality. */
const EXACT_MATCH: IsActiveMatchOptions = {
  paths: "exact",
  queryParams: "exact",
  fragment: "ignored",
  matrixParams: "ignored",
};

const NAV_GROUPS: readonly SidenavGroup[] = [
  {
    label: "Orders",
    items: [
      {
        label: "Orders",
        icon: "view_kanban",
        route: "/dashboard/board",
        activeOptions: SUBSET_MATCH,
        vetoedByPaymentView: true,
      },
      {
        label: "List",
        icon: "view_list",
        route: "/dashboard",
        activeOptions: EXACT_MATCH,
      },
      {
        label: "New Order",
        icon: "add_circle",
        route: "/orders/new",
        activeOptions: SUBSET_MATCH,
      },
      {
        label: "Customer Waiting",
        icon: "mark_email_unread",
        route: "/dashboard/customer-waiting",
        activeOptions: SUBSET_MATCH,
      },
      {
        label: "Messages",
        icon: "forum",
        route: "/messages",
        activeOptions: SUBSET_MATCH,
      },
    ],
  },
  {
    label: "Payments",
    requiresSuperAdmin: true,
    items: [
      {
        label: "Payments",
        icon: "payments",
        route: "/dashboard/board",
        // Selects the payment-lane view of the order board. Subset matching
        // (other board filter params may be present) keeps this link and the
        // plain Orders link from both reading as the payment view.
        queryParams: { viewMode: BOARD_VIEW_MODE.Payment },
        activeOptions: SUBSET_MATCH,
      },
      {
        label: "Record Check",
        icon: "receipt_long",
        route: "/payments/record-check",
        activeOptions: SUBSET_MATCH,
      },
    ],
  },
  {
    label: "Recordings",
    items: [
      {
        label: "Recordings",
        icon: "content_paste",
        route: "/dashboard/recordings",
        activeOptions: EXACT_MATCH,
      },
      {
        label: "Recording Notes",
        icon: "edit_note",
        route: "/dashboard/recordings/reference-notes",
        activeOptions: SUBSET_MATCH,
      },
    ],
  },
  {
    label: "Rules",
    requiresSuperAdmin: true,
    items: [
      {
        label: "Rule Browser",
        icon: "rule",
        route: "/rules",
        activeOptions: EXACT_MATCH,
      },
      {
        label: "Test an Order",
        icon: "science",
        route: "/rules/test",
        activeOptions: SUBSET_MATCH,
      },
      {
        label: "New Rule",
        icon: "add_circle",
        route: "/rules/new",
        activeOptions: SUBSET_MATCH,
      },
      {
        label: "New Batch",
        icon: "layers",
        route: "/rules/batches/new",
        activeOptions: SUBSET_MATCH,
      },
    ],
  },
];

@Component({
  selector: "admin-sidenav",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    RouterLinkActive,
    MatListModule,
    MatIconModule,
    MatButtonModule,
  ],
  template: `
    <div class="sidenav-header">
      <img
        class="sidenav-brand-logo"
        [src]="brandLogoSrc()"
        alt="DeedNetwork"
      />
      <button
        mat-icon-button
        class="sidenav-toggle"
        aria-label="Toggle navigation"
        (click)="toggleSidenav.emit()"
      >
        <mat-icon fontSet="material-symbols-rounded">menu</mat-icon>
      </button>
    </div>

    <mat-nav-list>
      @for (group of navGroups; track group.label) {
        <h3 matSubheader class="sidenav-subheader">{{ group.label }}</h3>
        @for (item of group.items; track item.route) {
          @let linkActive =
            rla.isActive && !(item.vetoedByPaymentView && paymentViewActive());
          <a
            mat-list-item
            [routerLink]="item.route"
            [queryParams]="item.queryParams"
            routerLinkActive
            #rla="routerLinkActive"
            [routerLinkActiveOptions]="item.activeOptions"
            [class.active-link]="linkActive"
          >
            <mat-icon
              matListItemIcon
              fontSet="material-symbols-rounded"
              [class.msr-filled]="linkActive"
              >{{ item.icon }}</mat-icon
            >
            <span matListItemTitle>{{ item.label }}</span>
          </a>
        }
      }
    </mat-nav-list>
  `,
  styles: [
    `
      :host {
        display: block;
        box-sizing: border-box;
        min-height: 100%;
        padding: 14px 12px 16px;
        background: var(--admin-sidenav-bg);
      }
      .sidenav-header {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 2px 8px;
      }
      .sidenav-brand-logo {
        height: 24px;
        max-width: 164px;
        object-fit: contain;
        object-position: left center;
      }
      .sidenav-toggle {
        margin-left: auto;
        color: var(--admin-sidenav-icon);
        --mat-icon-button-state-layer-size: 32px;
        --mat-icon-button-icon-size: 18px;
        --mat-icon-button-touch-target-display: none;
      }
      mat-icon.material-symbols-rounded {
        font-family: "Material Symbols Rounded";
        font-size: 18px;
        width: 18px;
        height: 18px;
        line-height: 1;
        font-variation-settings:
          "FILL" var(--msr-fill, 0),
          "wght" 400,
          "GRAD" 0,
          "opsz" 24;
      }
      mat-icon.msr-filled {
        --msr-fill: 1;
      }
      mat-nav-list {
        padding: 0;
        --mat-list-active-indicator-shape: 7px;
        --mat-list-list-item-one-line-container-height: 32px;
        --mat-list-list-item-label-text-size: 13.5px;
        --mat-list-list-item-label-text-color: var(--admin-sidenav-label);
        --mat-list-list-item-hover-label-text-color: var(--admin-sidenav-label);
        --mat-list-list-item-leading-icon-color: var(--admin-sidenav-icon);
        --mat-list-list-item-hover-leading-icon-color: var(
          --admin-sidenav-icon
        );
        --mat-list-list-item-leading-icon-size: 18px;
        --mat-list-list-item-leading-icon-start-space: 10px;
        --mat-list-list-item-leading-icon-end-space: 10px;
        --mat-list-list-item-hover-state-layer-opacity: 0;
      }
      a[mat-list-item] {
        margin-bottom: 1px;
        padding-right: 10px;
      }
      a[mat-list-item]:hover:not(.active-link) {
        background-color: var(--admin-sidenav-hover-bg);
      }
      .sidenav-subheader {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 14px 0 0;
        padding: 0 8px 6px;
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.12em;
        color: var(--admin-sidenav-group-label);
      }
      .sidenav-subheader::after {
        content: "";
        flex: 1;
        height: 1px;
        background: var(--admin-sidenav-border);
      }
      .active-link {
        background-color: var(--admin-sidenav-active-bg);
        --mat-list-list-item-label-text-color: var(
          --admin-sidenav-active-label
        );
        --mat-list-list-item-hover-label-text-color: var(
          --admin-sidenav-active-label
        );
        --mat-list-list-item-label-text-weight: 600;
        --mat-list-list-item-leading-icon-color: var(
          --admin-sidenav-active-icon
        );
        --mat-list-list-item-hover-leading-icon-color: var(
          --admin-sidenav-active-icon
        );
      }
    `,
  ],
})
export class SidenavComponent {
  private readonly tokenService = inject(TokenService);
  private readonly themeService = inject(ThemeService);
  private readonly route = inject(ActivatedRoute);

  toggleSidenav = output();

  /** Brand artwork tracking the active theme (white-text variant on dark). */
  readonly brandLogoSrc = computed(() =>
    this.themeService.isDark() ? BRAND_LOGO_DARK : BRAND_LOGO_LIGHT,
  );

  /**
   * True while the order board is showing the payment lanes. Vetoes the
   * Orders link's active pill so only the Payments link reads as active
   * on `?viewMode=payment`.
   */
  readonly paymentViewActive = toSignal(
    this.route.queryParamMap.pipe(
      map((params) => params.get("viewMode") === BOARD_VIEW_MODE.Payment),
    ),
    { initialValue: false },
  );

  /** Rules authoring is a super-admin-only area; hide its nav groups otherwise. */
  readonly isSuperAdmin =
    this.tokenService.decodeToken()?.roles?.includes(Roles.SUPER_ADMIN) ===
    true;

  /**
   * Nav content for the current user, filtered once at construction — the
   * role comes from the decoded token and never changes within a session.
   */
  readonly navGroups = NAV_GROUPS.filter(
    (group) => !group.requiresSuperAdmin || this.isSuperAdmin,
  );
}
